import { CarVisualization, MiwiAppServiceProvider } from '@oneaudi/miwi-feature-service'
import { CustomFeatureAppRenderingParams, FeatureAppLoader } from '@feature-hub/react'
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react'
import MiwiIcon from '../../assets/media/icons/miwi-button.svg'
import config from '../../config'
import Visualization from '../../entities/Visualization'
import MiwiFeatureServiceContext from '../../feature-app/MiwiFeatureServiceContext'
import useTranslation from '../../hooks/useTranslation'
import navigationStyle from '../navigation/navigation.css'
import style from './miwiFeatureAppLoader.css'
import filterRenderImagesByOrdering from '../../redux/selectors/render-image/helper/filterRenderImages'
import normalizeString, { normalizeUrl } from '../../helpers/normalizeString'

export type Props = {
    isMiwiEnabled: boolean
    shouldUseMiwiMod: boolean
    legacyAuthEnabled: boolean
    language: string | null
    visualization: Visualization
    consumptionData: string[]
    brand: string
    clientInformation: string
    renderImagesOrdering: string[]
    enableMiWiGrpTokenRefresh: boolean
}

export const normalizeConsumptionData = (consumptionData: string[]): string[] => {
    return consumptionData ?
        consumptionData
            .filter(Boolean)
            .map(data => data.replace(/<sub>(.*?)<\/sub>/g, "2").trim())
            .map(data => normalizeString(data))
        :
        []
}

const MiwiFeatureAppLoader: FunctionComponent<Props> = (props) => {
    const { isMiwiEnabled, shouldUseMiwiMod, legacyAuthEnabled, language, visualization, consumptionData, brand, clientInformation, renderImagesOrdering, enableMiWiGrpTokenRefresh } = props
    const miwiFS = React.useContext<MiwiAppServiceProvider>(MiwiFeatureServiceContext)
    const { t } = useTranslation()

    const [loadingError, setLoadingError] = useState(false)

    useEffect(() => {
        if (miwiFS && miwiFS.getMiwiService()) {
            const visualizationForMiwi: CarVisualization = {
                ...{
                    ...visualization,
                    renderImages: [],
                },
            }

            if (visualizationForMiwi.renderImages) {
                const filteredRenderImages = filterRenderImagesByOrdering(visualization.renderImages, renderImagesOrdering)

                visualizationForMiwi.renderImages = filteredRenderImages.map((renderImage) => {
                    const imageUrl = renderImage.renderDetails?.find(renderDetail => renderDetail.size === 3840).url
                    const normalizedUrl = normalizeUrl(imageUrl)
                    return {
                        id: renderImage.id,
                        urls: {
                            1920: normalizedUrl,
                            hd: normalizedUrl,
                        },
                        isActive: renderImage.isActive,
                    }
                })
            }

            visualizationForMiwi.consumptionData = normalizeConsumptionData(consumptionData)

            miwiFS.getMiwiService().updateVisualization(visualizationForMiwi)
        }
    }, [miwiFS, visualization, consumptionData, renderImagesOrdering])

    if (!isMiwiEnabled || loadingError) {
        return null
    }

    const backendSwitcherEnabled = (config.MIWI_FEATURE_BACKEND_SWITCHER === 'enabled') && !shouldUseMiwiMod

    const miwiAppConfig = {
        brand,
        lang: language,
        librarySrc: config.MIWI_FEATURE_LIBRARY_URL,
        visualization,
        backendSwitcher: backendSwitcherEnabled,
        clientInformation: shouldUseMiwiMod && legacyAuthEnabled ? 'legacyAuthEnabled' : clientInformation,
        parentEnvironment: config.AVP_STAGE,
        enableTokenRefresh: enableMiWiGrpTokenRefresh,
    }

    const triggerMiwiButtonClick = (event): void => {
        event.target.closest('button').parentNode.querySelector('#miwi-app-button').click()
    }

    const children = (childProps: CustomFeatureAppRenderingParams): ReactElement => (
        <div className={navigationStyle.link}>
            {childProps.featureAppNode}
            <button
                className={`${navigationStyle.link} ${style.miwiButtonWrapper} ${navigationStyle.miwibutton} ${
                    childProps.loading ? style.miwiButtonWrapperLoading : ''
                }`}
                onClick={triggerMiwiButtonClick}
                type="button">
                <MiwiIcon className={navigationStyle.icon} />
                <div className={navigationStyle.label}>{t('miwi.button-label')}</div>
                <div className={`${navigationStyle.trafficLight} ${style.trafficLight}`} />
            </button>
        </div>
    )

    const handleLoadingError = (error): void => {
        console.error(error)
        setLoadingError(true)
    }

    return (
        <>
            <FeatureAppLoader
                featureAppId="dasburo:dcc-app@dasburo:miwi-app"
                baseUrl={shouldUseMiwiMod ? config.MIWI_MOD_FEATURE_APP_URL : config.MIWI_FEATURE_APP_URL}
                src="/MiwiFeatureApp.umd.js"
                css={[{href: '/MiwiFeatureApp.css'}]}
                onError={handleLoadingError}
                config={miwiAppConfig}
            >
                {children}
            </FeatureAppLoader>
        </>
    )
}

export default MiwiFeatureAppLoader
